import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { formatOpening, relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Browser extension — pairing, the paired browsers, and the one sentence that
 * makes the whole design comprehensible.
 *
 * **The product statement is the point of this screen, not the device table.**
 * S-B14 moved LinkedIn discovery out of a server fleet and into the tenant's own
 * browser (PROGRESS `ARCH1`), and the consequence is that discovery runs only
 * while that browser is open. Its *Watch* is explicit that this "needs to be said
 * in the UI, or it reads as broken" — so it is said at the top, in plain words,
 * before anything the tenant has to do.
 *
 * **The code is shown once and never again.** It is a bearer credential with a
 * ten-minute life; re-reading it is not offered because the server cannot offer
 * it — only the digest is stored. Generating another takes one press.
 *
 * **Revoked devices stay in the list.** *"Revoked on 14 August"* is the answer to
 * a question tenants ask after the fact, and hiding the row makes the answer
 * unavailable.
 *
 * `last_seen_at` is what "your browser is open" is derived from, and it is
 * rendered as a relative time rather than as a green dot: *"last seen 4 minutes
 * ago"* is a fact the tenant can act on, and a dot is a claim the page cannot
 * actually make between polls.
 *
 * **The pause banner and the resume button are S-B16's, and they are the reason
 * this panel is where the notification's button now points.** Before them there
 * was no way out of a LinkedIn pause anywhere in the product: a challenge set
 * `connections.status` to `challenge`, nothing could set it back, and the email
 * sent a tenant to `/settings/connections`, which is S-B17's panel and does not
 * exist. Resuming is manual by design — `app/linkedin/pause.py` argues that any
 * automatic check would itself be a request against the session LinkedIn
 * interrupted — so the tenant clears the checkpoint in their own browser and
 * then asserts here that they have.
 *
 * The today-against-cap line is the same count the claim endpoint decides on, so
 * the panel cannot say *"12 of 50 used"* while discovery is being refused for a
 * spent budget.
 */

/** Matches `LIVE_BROWSER_SECONDS` in `api/app/v1/outreach.py` — three heartbeats. */
const LIVE_WINDOW_MS = 180_000

export function ExtensionPanel(): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const [code, setCode] = useState<{ value: string; expiresAt: string } | null>(null)
  const [confirming, setConfirming] = useState<{ id: string; label: string } | null>(null)
  const now = new Date()

  const devices = useQuery(
    (signal) => call(() => api.GET('/api/v1/extension/devices', { signal })),
    [],
  )

  const discovery = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/discovery', { signal })),
    [],
  )

  const pair = useMutation(() => call(() => api.POST('/api/v1/extension/pairings', {})))

  const resume = useMutation(() => call(() => api.POST('/api/v1/outreach/discovery/resume', {})))

  const revoke = useMutation((deviceId: string) =>
    call(() =>
      api.DELETE('/api/v1/extension/devices/{device_id}', {
        params: { path: { device_id: deviceId } },
      }),
    ),
  )

  const status = discovery.state.data
  const rows = devices.state.data?.devices ?? []
  const live = rows.filter(
    (row) =>
      row.revoked_at === null &&
      row.last_seen_at !== null &&
      now.getTime() - new Date(row.last_seen_at).getTime() < LIVE_WINDOW_MS,
  )

  return (
    <section className="ar-card" id="extension">
      <div className="ar-card__header">
        <h2>Browser extension</h2>
        <span className="ar-card__spacer" />
        <Button
          size="sm"
          busy={pair.pending}
          busyLabel="Generating"
          onClick={() => {
            void (async () => {
              const result = await pair.run(undefined)
              if (!result.ok) return
              setCode({ value: result.data.code, expiresAt: result.data.expires_at })
              devices.refetch()
            })()
          }}
        >
          Pair a browser
        </Button>
      </div>

      {/*
        Everything except the device table sits in a `.ar-card__body`, which is
        what puts the prose on the same inset as the heading above it. The table
        stays outside it and full-bleed — its cells carry their own padding, its
        row hover is meant to reach both edges, and `SessionsPanel` renders the
        same pair the same way.
      */}
      <div className="ar-card__body">
        <p className="ar-text-body-sm">
          {brand.productName} finds LinkedIn leads from <strong>your own browser</strong>, using the
          LinkedIn session you are already signed in with. Your LinkedIn password and session are
          never read, sent, or stored by {brand.productName}.
        </p>
        {/*
        The pause comes first, above everything else on the panel. An account
        LinkedIn has interrupted is not running, so the pairing instructions and
        the daily budget below are both answers to a question the tenant is not
        asking yet.
      */}
        {status?.paused === true ? (
          <div className="ar-callout" data-tone="danger">
            <p className="ar-text-label">LinkedIn discovery is paused</p>
            <p className="ar-text-body-sm">
              LinkedIn interrupted this account and we stopped rather than pushing on. Open LinkedIn
              in the browser you paired, clear whatever it is asking for, and then press resume.
              Nothing is lost — the keywords that were queued are waiting.
            </p>
            {status.paused_reason !== null ? (
              <p className="ar-text-body-sm ar-muted">LinkedIn said: {status.paused_reason}</p>
            ) : null}
            <Button
              busy={resume.pending}
              busyLabel="Resuming"
              onClick={() => {
                void (async () => {
                  const result = await resume.run(undefined)
                  if (!result.ok) return
                  discovery.refetch()
                  toast.show({ kind: 'success', title: 'Discovery resumed' })
                })()
              }}
            >
              I have cleared it — resume discovery
            </Button>
          </div>
        ) : null}

        {/*
        S-G2. The organisation's own switch, below the challenge banner and not
        above it — the same order `app/linkedin/limits.decide` reports the two
        refusals in, and for the same reason: a tenant knows they pressed
        *Pause*, and nobody has told them LinkedIn interrupted the account.
        Without this line the panel below would go on saying discovery runs
        whenever a browser is open, which under the switch is untrue.
      */}
        {status?.automation_paused === true ? (
          <div className="ar-callout" data-tone="warning">
            <p className="ar-text-label">All automation is paused for this organisation</p>
            <p className="ar-text-body-sm">
              Nothing is searching or posting, whether or not a browser is paired. Turn it back on
              in <Link to="/settings/danger-zone">Settings → Danger zone</Link>.
            </p>
          </div>
        ) : null}

        <p className="ar-text-body-sm ar-callout" data-tone="info">
          <strong>Discovery runs only while a paired browser is open.</strong>{' '}
          {/* One expression: JSX drops the newline after `{brand.productName}`,
              which printed "Limitedwaits" on the live tenant (2026-09-19). */}
          {`Close it, and ${brand.productName} waits — nothing is lost, and searching picks up where it left off next time.`}
          {/* The send window comes first: outside it nothing runs whether or
              not a browser is open, and a paused-for-the-weekend browser must
              not be described as gone (walkthrough 2026-09-12, a Saturday). */}
          {status?.window_open === false
            ? ` It is outside this organisation's send window (${status.window_sentence}) right now, so nothing searches until it opens${
                status.window_opens_at
                  ? ` on ${formatOpening(status.window_opens_at, 'Asia/Kolkata')} IST`
                  : ''
              } — whether or not a browser is open.`
            : live.length === 0 && rows.length > 0
              ? ' No paired browser has checked in recently, so nothing is running right now.'
              : null}
        </p>

        <InlineError failure={pair.state.failure ?? revoke.state.failure ?? resume.state.failure} />

        {code !== null ? (
          <div className="ar-callout" data-tone="success">
            <p className="ar-text-label">Type this into the browser extension</p>
            <p className="ar-code-display">{code.value}</p>
            <p className="ar-text-body-sm">
              It works once and expires {relativeTime(code.expiresAt, now)}. We cannot show it again
              — generate another if you need one.
            </p>
          </div>
        ) : null}

        {devices.state.status === 'loading' ? (
          <Loading label="Loading paired browsers" />
        ) : devices.state.status === 'error' ? (
          <ErrorState failure={devices.state.failure} onRetry={devices.refetch} />
        ) : rows.length === 0 ? (
          <p className="ar-empty ar-empty--inline">
            No browser is paired yet. Install <strong>the browser extension</strong> from the Chrome
            Web Store, then press <strong>Pair a browser</strong> above.
          </p>
        ) : null}
      </div>

      {/*
        The table, and only the table, outside the body — split out of the
        ternary above rather than nested in it, because a `.ar-table` inside a
        padded body is inset twice over and its row hover stops short of both
        card edges. `status === 'ready' && rows.length > 0` is the branch the
        ternary's `else` used to be: neither loading nor failing, with rows.
      */}
      {devices.state.status === 'ready' && rows.length > 0 ? (
        <div className="ar-table-wrap" tabIndex={0}>
          <table className="ar-table">
            <thead>
              <tr>
                <th scope="col">Browser</th>
                <th scope="col">Last seen</th>
                <th scope="col">Version</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-revoked={row.revoked_at !== null ? 'true' : undefined}>
                  <td>{row.label}</td>
                  <td>
                    {row.revoked_at !== null
                      ? `Disconnected ${relativeTime(row.revoked_at, now)}`
                      : row.last_seen_at === null
                        ? 'Not yet'
                        : relativeTime(row.last_seen_at, now)}
                  </td>
                  <td>{row.last_version ?? row.enrolled_version}</td>
                  <td>
                    {row.revoked_at === null ? (
                      <Button
                        tone="ghost"
                        size="sm"
                        onClick={() => {
                          setConfirming({ id: row.id, label: row.label })
                        }}
                      >
                        Disconnect
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="ar-card__body">
        <p className="ar-text-body-sm ar-muted">
          {brand.productName} runs at most {String(devices.state.data?.max_searches_per_day ?? 50)}{' '}
          LinkedIn searches a day for this organisation, with a pause between each one, and stops
          immediately if LinkedIn shows a security check.
          {status
            ? ` ${String(status.searches_today)} used today, ${String(status.searches_remaining_today)} left${
                status.queued > 0 ? `, ${String(status.queued)} waiting to run` : ''
              }.`
            : null}
        </p>
      </div>

      {/*
        Confirmed, because disconnecting is the one action here the tenant cannot
        undo from this screen: re-pairing needs a new code typed into the browser
        that was disconnected, which may be somewhere else entirely.
      */}
      {confirming !== null ? (
        <Modal
          title="Disconnect this browser?"
          onClose={() => {
            setConfirming(null)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setConfirming(null)
                }}
              >
                Keep it connected
              </Button>
              <Button
                tone="danger"
                busy={revoke.pending}
                busyLabel="Disconnecting"
                onClick={() => {
                  void (async () => {
                    const target = confirming
                    const result = await revoke.run(target.id)
                    setConfirming(null)
                    if (!result.ok) return
                    devices.refetch()
                    toast.show({ kind: 'success', title: 'Browser disconnected' })
                  })()
                }}
              >
                Disconnect
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            <strong>{confirming.label}</strong> will stop running LinkedIn searches for this
            organisation. To use it again you will need to pair it with a new code.
          </p>
        </Modal>
      ) : null}
    </section>
  )
}
